import { html, nothing } from "lit";
import { query, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import "@phosphor-icons/webcomponents/PhCopy";
import "@phosphor-icons/webcomponents/PhVideoCamera";
import "@phosphor-icons/webcomponents/PhVideoCameraSlash";
import "@phosphor-icons/webcomponents/PhMicrophone";
import "@phosphor-icons/webcomponents/PhMicrophoneSlash";
import "@phosphor-icons/webcomponents/PhMonitorArrowUp";
import "@phosphor-icons/webcomponents/PhCheck";
import "@phosphor-icons/webcomponents/PhX";
import "@phosphor-icons/webcomponents/PhWarning";
import "@phosphor-icons/webcomponents/PhCalendarBlank";
import "@phosphor-icons/webcomponents/PhClock";
import "@phosphor-icons/webcomponents/PhGlobeSimple";
import "ui-components/button/wow-button";
import "ui-components/form-elements/select/wow-select";
import "ui-components/collapsible-section/collapsible-section";
import "ui-components/centered-dialog/wow-centered-dialog";
import "ui-components/stream-recorder/stream-recorder";
import { StreamRecorder } from "ui-components/stream-recorder/stream-recorder";
import "ui-components/stream-player/cdn-player";
import "../../components/live-event-form/live-event-basic-info/live-event-basic-info";
import "../../components/live-event-form/live-event-preview/live-event-preview";
import "../../components/live-event-form/live-event-tickets-access/live-event-tickets-access";
import "../../components/live-event-form/live-event-optional-toggles/live-event-optional-toggles";
import { NotificationService } from "../../../../../libs/ui-components/notification";

import { styles } from "./live-event-form-page.css";
import { SignalElement, inject } from "ui-common/di";
import { LayoutController } from "ui-common/layout-controller";
import type { IAuthState } from "ui-common/services/auth/types";
import type {
  ILiveEventFormState,
  ILiveEventFormService,
  StreamSource,
} from "ui-common/services/live-event-form/types";
import type { ICreatorLiveEventsService } from "../../services/creator-live-events/types";

import { navigateTo } from "../../router";
import { t, translate } from "../../translations/i18n";

type PermissionState = "prompt" | "granted" | "denied" | "checking";

export abstract class LiveEventFormPage extends SignalElement {
  static override styles = [styles];

  @inject<IAuthState>("authState")
  protected authState!: IAuthState;

  @inject<ILiveEventFormState>("liveEventFormState")
  protected formState!: ILiveEventFormState;

  @inject<ILiveEventFormService>("liveEventFormService")
  protected formService!: ILiveEventFormService;

  @inject<ICreatorLiveEventsService>("creatorLiveEventsService")
  protected liveEventsService!: ICreatorLiveEventsService;

  @state()
  protected activeTab: "info" | "promotion" | "settings" = "info";

  @state()
  private showCancelConfirmation = false;

  private cancelConfirmResolver?: (result: boolean) => void;

  @state()
  private promotionTabVisited = false;

  @state()
  private cameraPermission: PermissionState = "checking";

  @state() private isCameraOn = true;
  @state() private isMicOn = true;
  @state() private isScreenSharing = false;
  private isScreenShareSupported = false;

  @query("stream-recorder")
  private streamRecorder?: StreamRecorder;

  protected isSaved = false;

  protected layoutController = new LayoutController(this);

  protected abstract get mode(): "create" | "edit";
  protected abstract get urlSegment(): string;
  protected abstract get desktopTitle(): ReturnType<typeof t>;
  protected abstract get streamSettingsButtonLabel(): ReturnType<typeof t>;
  protected abstract get isStreamSettingsTabEnabled(): boolean;
  protected abstract handleStreamSettingsAction(): void;

  protected get saveAndContinueLabel(): ReturnType<typeof t> {
    return t("createLiveEventPage.goToStreamSettings");
  }
  protected onSaveAndContinueSuccess(): void {
    // Default no-op; subclasses can override
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.isScreenShareSupported = StreamRecorder.isScreenShareSupported();

    this.formService.setTranslationFunction((key: string) => {
      return translate(key as "createLiveEventsModal.titleRequired");
    });

    if (this.layoutController.isMobile && this.formState.streamSource.get() === "obs") {
      this.formService.setStreamSource("webcam");
    }

    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const segmentIndex = pathSegments.indexOf(this.urlSegment);
    const eventId = segmentIndex !== -1 ? pathSegments[segmentIndex + 1] : undefined;

    if (eventId) {
      this.authState.ready.then(() =>
        this.formService.loadEvent(eventId).then(() => {
          this.promotionTabVisited = true;
          if (this.mode === "create") {
            this.activeTab = "settings";
          }
          if (this.activeTab === "settings" && this.formState.streamSource.get() === "webcam") {
            void this.checkCameraPermission();
          }
        }),
      );
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.formService.reset();
  }

  override async updated(changed: Map<PropertyKey, unknown>): Promise<void> {
    super.updated(changed);
    if (this.activeTab === "settings" && this.formState.streamSource.get() === "webcam") {
      await this.startStreamingIfNeeded();
    }
  }

  private async startStreamingIfNeeded(): Promise<void> {
    await this.updateComplete;
    if (this.streamRecorder && this.streamRecorder.streamId) {
      void this.streamRecorder.startStreaming();
    }
  }

  protected async handleSaveAndContinue(): Promise<void> {
    if (!this.formService.validateForm()) {
      NotificationService.error(translate("createLiveEventPage.fillRequiredFields"));
      return;
    }

    const success = await this.formService.saveEvent();
    if (success) {
      this.isSaved = true;
      this.formState.eventId.get();
      this.onSaveAndContinueSuccess();
      this.activeTab = "settings";
      if (this.formState.streamSource.get() === "webcam") {
        void this.checkCameraPermission();
      }
    }
  }

  async canDeactivate(): Promise<boolean> {
    if (this.isSaved) return true;
    return new Promise((resolve) => {
      this.cancelConfirmResolver = resolve;
      this.showCancelConfirmation = true;
    });
  }

  protected handleCancel(): void {
    this.showCancelConfirmation = true;
  }

  private handleCancelConfirmStay(): void {
    this.showCancelConfirmation = false;
    if (this.cancelConfirmResolver) {
      this.cancelConfirmResolver(false);
      this.cancelConfirmResolver = undefined;
    }
  }

  private handleCancelConfirmLeave(): void {
    this.showCancelConfirmation = false;
    if (this.cancelConfirmResolver) {
      this.cancelConfirmResolver(true);
      this.cancelConfirmResolver = undefined;
    } else {
      navigateTo("live-events");
    }
  }

  // --- Stream settings ---
  private get RTMP_URL(): string {
    const isLocalhost = (location.origin ?? "localhost").includes("localhost");
    const isProduction = location.origin.includes("worldofwow.com");
    const liveDomain = isLocalhost
      ? "live.worldofwow.dev"
      : isProduction
        ? "live.worldofwow.com"
        : "live.worldofwow.dev";
    return `rtmp://${liveDomain}/streams`;
  }

  private handleStreamSourceChange(e: CustomEvent<{ value: StreamSource }>) {
    const source = e.detail.value;
    this.formService.setStreamSource(source);
    if (source === "webcam") {
      void this.checkCameraPermission();
    }
  }

  protected async checkCameraPermission(): Promise<void> {
    try {
      this.cameraPermission = "checking";
      const result = await navigator.permissions.query({ name: "camera" as PermissionName });
      this.cameraPermission = result.state as PermissionState;
      result.addEventListener("change", () => {
        this.cameraPermission = result.state as PermissionState;
      });
    } catch {
      this.cameraPermission = "prompt";
    }
  }

  private async copyToClipboard(text: string, label: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      NotificationService.success(`${label} copied to clipboard`);
    } catch {
      NotificationService.error("Failed to copy");
    }
  }

  // --- Stream preview controls ---
  private handleToggleCamera(): void {
    if (!this.streamRecorder) return;
    this.streamRecorder.toggleCamera();
  }
  private handleCameraEnabled(): void {
    this.isCameraOn = true;
  }
  private handleCameraDisabled(): void {
    this.isCameraOn = false;
  }

  private handleToggleMicrophone(): void {
    if (!this.streamRecorder) return;
    this.streamRecorder.toggleMicrophone();
  }
  private handleMicrophoneEnabled(): void {
    this.isMicOn = true;
  }
  private handleMicrophoneDisabled(): void {
    this.isMicOn = false;
  }

  private handleToggleScreenShare(): void {
    if (!this.streamRecorder) return;
    if (this.isScreenSharing) {
      void this.streamRecorder.stopScreenShare();
    } else {
      void this.streamRecorder.startScreenShare();
    }
  }
  private handleScreenShareStarted(): void {
    this.isScreenSharing = true;
  }
  private handleScreenShareStopped(): void {
    this.isScreenSharing = false;
  }
  private handleScreenShareError(): void {
    this.isScreenSharing = false;
  }

  // --- Tab switching ---
  private handleTabClick(tab: "info" | "promotion" | "settings"): void {
    if (tab === "promotion" && !this.isPromotionTabEnabled) return;
    if (tab === "settings" && !this.isStreamSettingsTabEnabled) return;
    this.activeTab = tab;

    if (tab === "settings" && this.formState.streamSource.get() === "webcam") {
      void this.checkCameraPermission();
    }
  }

  // --- Render methods ---
  private get isPromotionTabEnabled(): boolean {
    return this.promotionTabVisited || !!this.formState.eventId.get();
  }

  private renderTabBar() {
    return html`
      <div class="tab-bar">
        <button
          class=${classMap({ "tab-button": true, active: this.activeTab === "settings" })}
          data-testid="stream-settings-tab"
          ?disabled=${!this.isStreamSettingsTabEnabled}
          @click=${() => this.handleTabClick("settings")}
        >
          ${t("createLiveEventPage.tabStreamSettings")}
        </button>
        <button
          class=${classMap({ "tab-button": true, active: this.activeTab === "info" })}
          data-testid="event-info-tab"
          @click=${() => this.handleTabClick("info")}
        >
          ${t("createLiveEventPage.tabEventInfo")}
        </button>
        ${this.layoutController.isMobile
          ? html`
              <button
                class=${classMap({ "tab-button": true, active: this.activeTab === "promotion" })}
                data-testid="promotion-page-tab"
                ?disabled=${!this.isPromotionTabEnabled}
                @click=${() => this.handleTabClick("promotion")}
              >
                ${t("createLiveEventPage.tabPromotionPage")}
              </button>
            `
          : nothing}
      </div>
    `;
  }

  private renderEventInfoTab() {
    const isLoading = this.formState.isLoading.get();

    if (isLoading) {
      return html`<div class="loading">${t("createLiveEventPage.loadingEvent")}</div>`;
    }

    if (this.layoutController.isMobile) {
      return html`
        <div class="event-info-tab">
          <live-event-basic-info></live-event-basic-info>
          <hr class="section-divider" />
          <live-event-tickets-access></live-event-tickets-access>
        </div>
      `;
    }

    return html`
      <form id="live-event-form" class="two-column">
        <div class="desktop-preview-fields">
          <live-event-preview .isMobile=${false}></live-event-preview>
        </div>

        <div class="form-panel">
          <live-event-basic-info></live-event-basic-info>
          <hr class="section-divider" />
          <live-event-tickets-access></live-event-tickets-access>
          <live-event-optional-toggles></live-event-optional-toggles>
        </div>

        <div class="preview-panel">
          <span class="preview-label">${t("createLiveEventPage.promotionPage")}</span>
          <span class="preview-subtitle">${t("createLiveEventPage.promotionPageSubtitle")}</span>
        </div>
      </form>
    `;
  }

  private renderPromotionTab() {
    return html`
      <div class="promotion-tab">
        <live-event-preview .isMobile=${this.layoutController.isMobile}></live-event-preview>
        <live-event-optional-toggles></live-event-optional-toggles>
      </div>
    `;
  }

  private handleGoToPromotionTab(): void {
    if (!this.formService.validateInfoTab()) {
      NotificationService.error(translate("createLiveEventPage.fillRequiredFields"));
      return;
    }
    this.promotionTabVisited = true;
    this.activeTab = "promotion";
  }

  private renderEventInfoFooter() {
    const isSubmitting = this.formState.isSubmitting.get();

    if (this.layoutController.isMobile) {
      return html`
        <div class="page-footer">
          <div class="footer-actions">
            <wow-button variant="neutral" size="small" @click=${this.handleCancel}
              >${t("createLiveEventPage.cancel")}</wow-button
            >
            <wow-button variant="primary" size="small" @click=${this.handleGoToPromotionTab}>
              ${t("createLiveEventPage.goToPromotionPage")}
            </wow-button>
          </div>
        </div>
      `;
    }

    return html`
      <div class="page-footer">
        <div class="footer-actions">
          <wow-button variant="neutral" size="small" @click=${this.handleCancel}
            >${t("createLiveEventPage.cancel")}</wow-button
          >
          <wow-button variant="primary" size="small" ?disabled=${isSubmitting} @click=${this.handleSaveAndContinue}>
            ${isSubmitting ? t("createLiveEventPage.saving") : this.saveAndContinueLabel}
          </wow-button>
        </div>
      </div>
    `;
  }

  private renderPromotionFooter() {
    const isSubmitting = this.formState.isSubmitting.get();

    return html`
      <div class="page-footer">
        <div class="footer-actions">
          <wow-button variant="neutral" size="small" @click=${this.handleCancel}
            >${t("createLiveEventPage.cancel")}</wow-button
          >
          <wow-button variant="primary" size="small" ?disabled=${isSubmitting} @click=${this.handleSaveAndContinue}>
            ${isSubmitting ? t("createLiveEventPage.saving") : this.saveAndContinueLabel}
          </wow-button>
        </div>
      </div>
    `;
  }

  private renderStreamSettingsTab() {
    if (this.layoutController.isMobile) {
      return this.renderStreamPreview();
    }

    return html`
      <div class="two-column">
        <div class="form-panel">
          <wow-collapsible-section
            collapsible
            expanded
            header=${t("createLiveEventPage.chooseStreamSource")}
            subtitle=${t("createLiveEventPage.chooseStreamSourceSubtitle")}
          >
            <div class="stream-settings-container">
              <wow-select
                label=${t("createLiveEventPage.selectSource")}
                .options=${[
                  { value: "webcam", label: translate("createLiveEventPage.computerWebcam") },
                  { value: "obs", label: "OBS Studio" },
                ]}
                .value=${this.formState.streamSource.get()}
                @change=${this.handleStreamSourceChange}
              ></wow-select>
              ${this.formState.streamSource.get() === "webcam" ? this.renderWebcamConfig() : this.renderObsConfig()}
            </div>
          </wow-collapsible-section>
        </div>

        <!-- Right column: Stream Preview -->
        <div class="stream-preview-panel">
          <div class="stream-preview-header">
            <span class="stream-preview-label">${t("createLiveEventPage.streamPreview")}</span>
          </div>
          ${this.renderStreamPreview()}
        </div>
      </div>
    `;
  }

  private renderStreamPreview() {
    return html`
      <div class="stream-preview-area">
        ${this.formState.streamSource.get() === "webcam"
          ? html`
              <stream-recorder
                .streamId=${this.formState.eventId.get() ?? this.formState.provisionalEventId.get()}
                show-preview
                hide-controls
                @camera-enabled=${this.handleCameraEnabled}
                @camera-disabled=${this.handleCameraDisabled}
                @microphone-enabled=${this.handleMicrophoneEnabled}
                @microphone-disabled=${this.handleMicrophoneDisabled}
                @screen-share-started=${this.handleScreenShareStarted}
                @screen-share-stopped=${this.handleScreenShareStopped}
                @screen-share-error=${this.handleScreenShareError}
              ></stream-recorder>
              <div class="preview-controls">
                <wow-button
                  variant=${this.isCameraOn ? "neutral" : "danger"}
                  size="small"
                  square
                  rounded
                  @click=${this.handleToggleCamera}
                >
                  ${this.isCameraOn
                    ? html`<ph-video-camera size="18"></ph-video-camera>`
                    : html`<ph-video-camera-slash size="18"></ph-video-camera-slash>`}
                </wow-button>
                <wow-button
                  variant=${this.isMicOn ? "neutral" : "danger"}
                  size="small"
                  square
                  rounded
                  @click=${this.handleToggleMicrophone}
                >
                  ${this.isMicOn
                    ? html`<ph-microphone size="18"></ph-microphone>`
                    : html`<ph-microphone-slash size="18"></ph-microphone-slash>`}
                </wow-button>
                ${this.isScreenShareSupported
                  ? html`
                      <wow-button
                        variant=${this.isScreenSharing ? "primary" : "neutral"}
                        size="small"
                        square
                        rounded
                        @click=${this.handleToggleScreenShare}
                      >
                        <ph-monitor-arrow-up
                          size="18"
                          weight=${this.isScreenSharing ? "fill" : "regular"}
                        ></ph-monitor-arrow-up>
                      </wow-button>
                    `
                  : nothing}
              </div>
            `
          : html`<cdn-player
              .streamId=${this.formState.eventId.get() ?? this.formState.provisionalEventId.get()}
            ></cdn-player>`}
      </div>
    `;
  }

  private renderWebcamConfig() {
    return html` <div class="form-section">${this.renderPermissionStatus()}</div> `;
  }

  private renderObsConfig() {
    const eventId = this.formState.eventId.get() ?? this.formState.provisionalEventId.get();
    if (!eventId) return nothing;

    return html`
      <div class="obs-config-content">
        <div class="obs-steps">
          <div class="obs-step">
            <div class="step-number">1</div>
            <div class="step-content">
              <div class="step-title">${t("createLiveEventPage.obsStepConnect")}</div>
              <div class="step-description">${t("createLiveEventPage.obsStep1")}</div>
            </div>
          </div>

          <div class="obs-step">
            <div class="step-number">2</div>
            <div class="step-content">
              <div class="step-title">${t("createLiveEventPage.serverUrl")}</div>

              <div class="obs-credential-row">
                <code class="obs-credential-value">${this.RTMP_URL}</code>
                <wow-button
                  variant="subtle"
                  size="small"
                  square
                  @click=${() => this.copyToClipboard(this.RTMP_URL, "URL")}
                >
                  <ph-copy size="16"></ph-copy>
                </wow-button>
              </div>

              <div class="step-title">${t("createLiveEventPage.streamKey")}</div>

              <div class="obs-credential-row">
                <code class="obs-credential-value masked">${"•".repeat(40)}</code>
                <wow-button
                  variant="subtle"
                  size="small"
                  square
                  @click=${() => this.copyToClipboard(eventId, "Stream Key")}
                >
                  <ph-copy size="16"></ph-copy>
                </wow-button>
              </div>
            </div>
          </div>

          <div class="obs-step">
            <div class="step-number">3</div>
            <div class="step-content">
              <div class="step-title">${t("createLiveEventPage.obsStepCheck")}</div>
              <div class="step-description">
                ${t("createLiveEventPage.obsStep3")}
                <a href="#" class="problem-link">${t("createLiveEventPage.reportProblem")}</a>.
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderPermissionStatus() {
    const statusClasses = {
      "permission-status": true,
      [this.cameraPermission]: true,
    };

    switch (this.cameraPermission) {
      case "checking":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-video-camera size="20"></ph-video-camera>
            <span>${t("streamConfigModal.checkingPermissions")}</span>
          </div>
        `;
      case "granted":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-check size="20" weight="bold"></ph-check>
            <span>${t("streamConfigModal.cameraAccessGranted")}</span>
          </div>
        `;
      case "denied":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-x size="20" weight="bold"></ph-x>
            <span>${t("streamConfigModal.cameraAccessDenied")}</span>
          </div>
        `;
      case "prompt":
        return html`
          <div class=${classMap(statusClasses)}>
            <ph-warning size="20"></ph-warning>
            <span>${t("streamConfigModal.cameraPermissionPrompt")}</span>
          </div>
        `;
    }
  }

  private renderStreamSettingsFooter() {
    const isSubmitting = this.formState.isSubmitting.get();

    return html`
      <div class="page-footer">
        <div class="footer-actions">
          <wow-button variant="neutral" size="small" @click=${this.handleCancel}
            >${t("createLiveEventPage.cancel")}</wow-button
          >
          <wow-button
            variant="primary"
            size="small"
            ?disabled=${isSubmitting}
            @click=${this.handleStreamSettingsAction}
          >
            ${isSubmitting ? t("createLiveEventPage.saving") : this.streamSettingsButtonLabel}
          </wow-button>
        </div>
      </div>
    `;
  }

  private renderActiveTab() {
    switch (this.activeTab) {
      case "info":
        return this.renderEventInfoTab();
      case "promotion":
        return this.renderPromotionTab();
      case "settings":
        return this.renderStreamSettingsTab();
    }
  }

  private renderActiveFooter() {
    switch (this.activeTab) {
      case "info":
        return this.renderEventInfoFooter();
      case "promotion":
        return this.renderPromotionFooter();
      case "settings":
        return this.renderStreamSettingsFooter();
    }
  }

  override render() {
    return html`
      <div class="page-container">
        <div class="page-header">
          <h1 class="page-title desktop-title">${this.desktopTitle}</h1>
          <h1 class="page-title mobile-title">${t("createLiveEventPage.liveEvent")}</h1>
          <p class="page-subtitle">${t("createLiveEventPage.subtitle")}</p>
        </div>

        ${this.renderTabBar()} ${this.renderActiveTab()}
      </div>

      ${this.renderActiveFooter()}
      ${this.showCancelConfirmation
        ? html`
            <wow-centered-dialog
              header=${t("createLiveEventPage.cancelConfirmTitle")}
              description=${t("createLiveEventPage.cancelConfirmDescription")}
              @close=${this.handleCancelConfirmStay}
            >
              <wow-button variant="primary" size="medium" @click=${this.handleCancelConfirmStay}>
                ${t("createLiveEventPage.cancelConfirmStay")}
              </wow-button>
              <wow-button variant="neutral" size="medium" @click=${this.handleCancelConfirmLeave}>
                ${t("createLiveEventPage.cancelConfirmLeave")}
              </wow-button>
            </wow-centered-dialog>
          `
        : nothing}
    `;
  }
}
